import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
  Cell,
} from 'recharts';
import { Package, Loader2, AlertTriangle } from 'lucide-react';

/**
 * Interface for inventory data point
 */
export interface InventoryData {
  name: string;
  quantity: number;
  maxQuantity: number;
  type?: string;
}

interface InventoryChartProps {
  data: InventoryData[];
  loading?: boolean;
  error?: string | null;
  height?: number;
}

/**
 * Get stock level color based on quantity
 */
const getStockColor = (quantity: number, maxQuantity: number): string => {
  const percentage = (quantity / maxQuantity) * 100;
  if (percentage >= 60) return '#10b981'; // green - good
  if (percentage >= 30) return '#f59e0b'; // amber - warning
  return '#ef4444'; // red - critical
};

/**
 * Get stock status text
 */
const getStockStatus = (quantity: number, maxQuantity: number): string => {
  const percentage = (quantity / maxQuantity) * 100;
  if (percentage >= 60) return 'Good';
  if (percentage >= 30) return 'Low';
  return 'Critical';
};

/**
 * Custom tooltip for inventory chart
 */
const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const item = payload[0].payload;
    const percentage = ((item.quantity / item.maxQuantity) * 100).toFixed(1);
    const status = getStockStatus(item.quantity, item.maxQuantity);

    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-700 mb-2">{item.name}</p>
        <div className="space-y-1">
          <p className="text-sm text-gray-600">
            Current Stock: <span className="font-bold">{item.quantity}</span>
          </p>
          <p className="text-sm text-gray-600">
            Max Capacity: <span className="font-bold">{item.maxQuantity}</span>
          </p>
          <p className="text-sm text-gray-600">
            Stock Level: <span className="font-bold">{percentage}%</span>
          </p>
          <p className={`text-sm font-semibold ${
            status === 'Good' ? 'text-green-600' :
            status === 'Low' ? 'text-yellow-600' : 'text-red-600'
          }`}>
            Status: {status}
          </p>
        </div>
      </div>
    );
  }
  return null;
};

/**
 * InventoryChart - Display general inventory stock levels
 */
const InventoryChart: React.FC<InventoryChartProps> = ({
  data,
  loading = false,
  error = null,
  height = 350,
}) => {
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading inventory data...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <p className="text-red-600 font-semibold">Failed to load chart</p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center text-gray-500">
          <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No inventory data available</p>
        </div>
      </div>
    );
  }

  // Calculate low stock items
  const lowStockItems = data.filter(item => {
    const percentage = (item.quantity / item.maxQuantity) * 100;
    return percentage < 30;
  });

  const criticalStockItems = data.filter(item => {
    const percentage = (item.quantity / item.maxQuantity) * 100;
    return percentage < 15;
  });

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <Package className="w-5 h-5 mr-2 text-blue-600" />
          General Inventory Status
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Stock levels across all inventory items
        </p>
      </div>

      {/* Alert Section */}
      {lowStockItems.length > 0 && (
        <div className="mb-4 bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
            <div>
              <p className="text-sm font-semibold text-yellow-800">
                {lowStockItems.length} item{lowStockItems.length > 1 ? 's' : ''} running low
              </p>
              {criticalStockItems.length > 0 && (
                <p className="text-xs text-yellow-700 mt-1">
                  {criticalStockItems.length} at critical levels
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-green-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Good Stock</p>
          <p className="text-lg font-bold text-green-600">
            {data.filter(item => (item.quantity / item.maxQuantity) * 100 >= 60).length}
          </p>
        </div>
        <div className="bg-yellow-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Low Stock</p>
          <p className="text-lg font-bold text-yellow-600">
            {data.filter(item => {
              const pct = (item.quantity / item.maxQuantity) * 100;
              return pct >= 30 && pct < 60;
            }).length}
          </p>
        </div>
        <div className="bg-red-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Critical</p>
          <p className="text-lg font-bold text-red-600">
            {data.filter(item => (item.quantity / item.maxQuantity) * 100 < 30).length}
          </p>
        </div>
      </div>

      {/* Chart - Simplified */}
      <ResponsiveContainer width="100%" height={height - 150}>
        <BarChart
          data={data}
          margin={{ top: 10, right: 20, left: 80, bottom: 10 }}
          layout="vertical"
        >
          <CartesianGrid strokeDasharray="0" stroke="transparent" />
          <XAxis type="number" stroke="#999" style={{ fontSize: '12px' }} />
          <YAxis dataKey="name" type="category" stroke="#666" width={75} style={{ fontSize: '13px' }} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(200, 200, 200, 0.1)' }} />
          <Bar dataKey="quantity" radius={[0, 6, 6, 0]} animationDuration={500}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getStockColor(entry.quantity, entry.maxQuantity)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default InventoryChart;
